import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials } from '../mockData';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  const navigateTestimonial = (newDirection) => {
    setDirection(newDirection);
    if (newDirection === 1) {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    } else {
      setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    }
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  return (
    <section id="testimonials" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900"></div>
      
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12 sm:mb-16 px-4"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 mb-4">
            Client Success Stories
          </h2>
          <div className="w-24 sm:w-32 h-1 bg-gradient-to-r from-cyan-500 to-blue-600 mx-auto neon-glow"></div>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="relative flex items-center">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              className="w-full"
            >
              <div className="relative p-8 sm:p-12 min-h-[320px] flex items-center bg-gradient-to-br from-slate-900/80 to-slate-800/80 rounded-3xl border border-cyan-500/30 backdrop-blur-sm">
                {/* Quote Icon */}
                <div className="absolute top-6 left-6 w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center opacity-20">
                  <Quote size={24} />
                </div>

                <div className="relative z-10 text-center w-full">
                  {/* Quote */}
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="text-lg sm:text-xl text-gray-300 font-light leading-relaxed mb-6 italic px-4 max-w-4xl mx-auto"
                  >
                    "{testimonials[currentIndex].quote}"
                  </motion.p>

                  {/* Author Info */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="pt-4 border-t border-cyan-500/20 inline-block"
                  >
                    <div className="text-lg font-bold text-white mb-1">
                      {testimonials[currentIndex].name}
                    </div>
                    <div className="text-cyan-400 text-sm font-medium">
                      {testimonials[currentIndex].role}
                    </div>
                  </motion.div>
                </div>

                {/* Decorative corners */}
                <div className="absolute top-0 left-0 w-24 h-24 border-l-2 border-t-2 border-cyan-500/30 rounded-tl-3xl"></div>
                <div className="absolute bottom-0 right-0 w-24 h-24 border-r-2 border-b-2 border-purple-500/30 rounded-br-3xl"></div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Buttons - Fixed positioning */}
          <button
            onClick={() => navigateTestimonial(-1)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-16 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center neon-glow z-20 transition-transform hover:scale-110 active:scale-95"
          >
            <ChevronLeft size={24} className="text-white" />
          </button>

          <button
            onClick={() => navigateTestimonial(1)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-16 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center neon-glow z-20 transition-transform hover:scale-110 active:scale-95"
          >
            <ChevronRight size={24} className="text-white" />
          </button>
        </div>

        {/* Indicators */}
        <div className="flex justify-center gap-3 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setDirection(index > currentIndex ? 1 : -1);
                setCurrentIndex(index);
              }}
              className={`h-3 rounded-full transition-all ${
                index === currentIndex
                  ? 'bg-cyan-500 w-12 neon-glow'
                  : 'bg-gray-600 hover:bg-gray-500 w-3'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
